import React from "react";
import { Box, Typography } from "@mui/material";
import { Code as CodeIcon } from "@mui/icons-material";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { oneDark } from "@codemirror/theme-one-dark";

const CodeEditor = ({
  value = "",
  onChange,
  placeholder = "// Write your JavaScript function here...",
  label,
  helperText,
  error = false,
  disabled = false,
  height = "120px",
  theme = "light",
  ...props
}) => {
  const handleChange = (val) => {
    if (onChange) {
      // Create a synthetic event-like object for compatibility with existing code
      const syntheticEvent = {
        target: { value: val },
        preventDefault: () => {},
        stopPropagation: () => {},
      };
      onChange(syntheticEvent);
    }
  };

  const extensions = [javascript()];
  const selectedTheme = theme === "dark" ? oneDark : undefined;

  return (
    <Box sx={{ width: "100%" }}>
      {label && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CodeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: error ? "error.main" : disabled ? "text.disabled" : "text.primary",
            }}
          >
            {label}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          border: "1px solid",
          borderColor: error ? "error.main" : disabled ? "action.disabled" : "divider",
          borderRadius: 1,
          overflow: "hidden",
          backgroundColor: disabled ? "action.hover" : "background.paper",
          "&:hover": !disabled && {
            borderColor: "primary.main",
          },
          "&:focus-within": !disabled && {
            borderColor: "primary.main",
            borderWidth: "2px",
          },
        }}
      >
        <CodeMirror
          value={value}
          height={height}
          placeholder={placeholder}
          onChange={handleChange}
          editable={!disabled}
          extensions={extensions}
          theme={selectedTheme}
          basicSetup={{
            lineNumbers: true,
            foldGutter: false,
            dropCursor: false,
            allowMultipleSelections: false,
            indentOnInput: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            highlightSelectionMatches: false,
            searchKeymap: false,
          }}
          style={{
            fontSize: "14px",
            fontFamily: '"Fira Code", "Monaco", "Cascadia Code", "Roboto Mono", monospace',
          }}
          {...props}
        />
      </Box>

      {helperText && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            display: "block",
            color: error ? "error.main" : disabled ? "text.disabled" : "text.secondary",
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default CodeEditor;
